import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { fromJS, List } from 'immutable';
import { has, isBoolean, keys, mapKeys, reduce } from 'lodash';
import {
  ALL_LIST_FILTER,
  IFetchFilterKey,
  ISearchParams,
  SEARCH_LIST_FILTER,
  SearchParamsEnum,
  SortByEnum,
  SortOrderEnum,
} from '../entity-data/entity-data.data';
import {
  getInContent,
  IS_DIRTY_KEY,
  IS_NEW_KEY,
  setInContent,
} from '../entity-data/entity.model';
import { ISelectOption } from '../form-master/components/select-field/select-field.interface';
import { Settings } from '../settings-data/settings.model';
import {
  ITEM_LIST_SEARCH_PARAMS_KEY,
  ItemFiltersEnum,
  ItemUnitTypesEnum,
  LABOR_ITEM_LIST_SEARCH_PARAMS_KEY,
  PARTS_ITEM_LIST_SEARCH_PARAMS_KEY,
} from './item-data.data';
import { Item } from './item.model';
import { EntityDataBuilder } from '../entity-data/entity-data.builder';
import { EntityTypesEnum } from '../../app.enum';
import { UrlService } from '../shared/services/url/url.service';
import { AccountQuotas } from '../features-data/sub-models/account-quotas.model';

@Injectable()
export class ItemDataBuilder extends EntityDataBuilder<Item> {
  model = Item;
  entityType: EntityTypesEnum = EntityTypesEnum.product;
  quotaName = AccountQuotas.KEYS.PRODUCTS;
  listFilterKeys: IFetchFilterKey = {
    [ALL_LIST_FILTER]: ITEM_LIST_SEARCH_PARAMS_KEY,
    [ItemFiltersEnum.parts]: PARTS_ITEM_LIST_SEARCH_PARAMS_KEY,
    [ItemFiltersEnum.labor]: LABOR_ITEM_LIST_SEARCH_PARAMS_KEY,
  };

  constructor(
    private _translateService: TranslateService,
    urlService: UrlService
  ) {
    super(urlService);
  }

  buildEntity(
    props: any = {},
    settings: Settings,
    isDirty?: boolean,
    isNew?: boolean
  ): Item {
    const localeKey = Item.CONTENT_KEYS.LOCALE;
    const currencyCodeKey = Item.CONTENT_KEYS.CURRENCY_CODE;

    props = props && props.toMap ? props.toMap() : fromJS(props || {});

    const itemProps = props.withMutations((map) => {
      // Default locale
      if (!getInContent(map, localeKey)) {
        setInContent(map, localeKey, settings.documentLocale);
      }

      // Default currency code
      if (!getInContent(map, currencyCodeKey)) {
        setInContent(map, currencyCodeKey, settings.documentCurrencyCode);
      }

      if (isBoolean(isDirty)) {
        map.set(IS_DIRTY_KEY, isDirty);
      }

      if (isBoolean(isNew)) {
        map.set(IS_NEW_KEY, isNew);
      }
    });

    return new Item(itemProps);
  }

  buildEntities(array) {
    return List<Item>(array ? array.map(this.buildEntity.bind(this)) : []);
  }

  buildSortItemsComparatorFn(
    sortBy: SortByEnum,
    sortOrder: SortOrderEnum
  ): (itemA: Item, itemB: Item) => number {
    const order = sortOrder === SortOrderEnum.asc ? 1 : -1;

    const codeSort = (itemA: Item, itemB: Item): number => {
      const itemCodeA = (itemA.code || '').toLowerCase();
      const itemCodeB = (itemB.code || '').toLowerCase();

      if (itemCodeA < itemCodeB) {
        return order * -1;
      } else if (itemCodeA > itemCodeB) {
        return order;
      } else {
        return 0;
      }
    };

    const descriptionSort = (itemA: Item, itemB: Item): number => {
      const itemDescriptionA = (itemA.name || '').toLowerCase();
      const itemDescriptionB = (itemB.name || '').toLowerCase();

      if (itemDescriptionA < itemDescriptionB) {
        return order * -1;
      } else if (itemDescriptionA > itemDescriptionB) {
        return order;
      } else {
        return 0;
      }
    };

    switch (sortBy) {
      case SortByEnum.code:
        return codeSort;
      case SortByEnum.description:
        return descriptionSort;
      case SortByEnum.code:
        return (itemA: Item, itemB: Item): number => {
          const itemCodeA = itemA.code || '';
          const itemCodeB = itemB.code || '';

          if (itemCodeA < itemCodeB) {
            return order * -1;
          } else if (itemCodeA > itemCodeB) {
            return order;
          } else {
            return descriptionSort(itemA, itemB);
          }
        };
      default:
        return null;
    }
  }

  buildListItemsFilterFn(
    searchParams: ISearchParams,
    listFilter: string,
    settings: Settings
  ): (entity: Item) => boolean {
    if (settings.separatePartsAndLabor && listFilter !== SEARCH_LIST_FILTER) {
      return listFilter === ItemFiltersEnum.parts
        ? (item: Item) => item.unitType === ItemUnitTypesEnum.parts
        : (item: Item) => item.unitType !== ItemUnitTypesEnum.parts;
    }

    return (item: Item) => true;
  }

  buildListFilter(entity: any, settings: Settings): string {
    const item: Item = entity as Item;

    if (settings.separatePartsAndLabor) {
      return item.unitType === ItemUnitTypesEnum.parts
        ? ItemFiltersEnum.parts
        : ItemFiltersEnum.labor;
    }

    return ALL_LIST_FILTER;
  }

  buildSearchParams(
    currentSearchParams: ISearchParams,
    newSearchParams: ISearchParams,
    listFilter?: string
  ): ISearchParams {
    const commonParams = super.buildSearchParams(
      currentSearchParams,
      newSearchParams,
      listFilter
    );

    return mapKeys(commonParams, (value, key) =>
      key === SearchParamsEnum.list_category ? SearchParamsEnum.unit_type : key
    );
  }

  validateSearchParams(searchParams: ISearchParams, filter: string) {
    const validParams = {
      [SearchParamsEnum.sort_by]: (a) => [SortByEnum.description].includes(a),
      [SearchParamsEnum.sort_order]: (a) =>
        [SortOrderEnum.asc, SortOrderEnum.desc].includes(a),
    };
    const requiredKeys = keys(validParams).every((key) =>
      has(searchParams, key)
    );
    const validValues = reduce(
      searchParams,
      (result, value, key) =>
        result && has(validParams, key) && validParams[key](value),
      true
    );

    return requiredKeys && validValues;
  }

  buildUnitTypeOptions(separatePartsAndLabor: boolean): List<ISelectOption> {
    if (separatePartsAndLabor) {
      return List([
        {
          key: this._translateService.instant('LABEL.PARTS'),
          value: ItemUnitTypesEnum.parts,
          trackingValue: 'parts',
        },
        {
          key: this._translateService.instant('LABEL.LABOR'),
          value: ItemUnitTypesEnum.labor,
          trackingValue: 'labor',
        },
        {
          key: this._translateService.instant('LABEL.HOURS'),
          value: ItemUnitTypesEnum.labor_hours,
          trackingValue: 'hours',
        },
        {
          key: this._translateService.instant('LABEL.DAYS'),
          value: ItemUnitTypesEnum.labor_days,
          trackingValue: 'days',
        },
      ]);
    } else {
      return List([
        {
          key: this._translateService.instant('LABEL.NONE'),
          value: ItemUnitTypesEnum.parts,
          trackingValue: 'none',
        },
        {
          key: this._translateService.instant('LABEL.HOURS'),
          value: ItemUnitTypesEnum.labor_hours,
          trackingValue: 'hours',
        },
        {
          key: this._translateService.instant('LABEL.DAYS'),
          value: ItemUnitTypesEnum.labor_days,
          trackingValue: 'days',
        },
      ]);
    }
  }
}
